// react_site/src/pages/GeneratorPage.jsx
import { useState, useContext, useRef, useEffect } from 'react';
import {
    Box, Typography, Button, Sheet, Stack, Alert, Select, Option,
    FormControl, FormLabel, LinearProgress, Card,
    Checkbox, Chip, Grid, List, ListItem, ListItemContent,
    Accordion, AccordionGroup, AccordionSummary, AccordionDetails,
    Input, Divider
} from '@mui/joy';
import { UploadSimple, ChartLine, Sparkle, FloppyDisk, Trash, CheckCircle, Plus, Folder, ArrowsClockwise } from "@phosphor-icons/react";

import { LayoutContext } from '../context';
import {
    APIUploadPDF,
    APIAnalyzePDFPages,
    APIGenerateQuestions,
    APIImportGeneratedQuestions,
    APIImportToQgen,
    APICleanupGenerator,
    APIGetContentTree,
    APICreateCategory,
    APIGenerateLoop,
    APIGenerateClinicalCases,
    APIImportClinicalCases
} from '../api';

const PAGE_COUNT_OPTIONS = [
    { value: 10, label: '10 pages' },
    { value: 25, label: '25 pages' },
    { value: 50, label: '50 pages' },
    { value: null, label: 'All pages' },
    { value: 'custom', label: 'Custom...' },
];

export default function GeneratorPage() {
    const { token, user } = useContext(LayoutContext);

    // State
    const [step, setStep] = useState(1); // 1: Upload, 2: Analyze, 3: Generate, 4: Import
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // PDF state
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfInfo, setPdfInfo] = useState(null);
    const fileInputRef = useRef(null);

    // Analysis state
    const [startPage, setStartPage] = useState(0);
    const [pageCount, setPageCount] = useState(10);
    const [customPageCount, setCustomPageCount] = useState('');
    const [chapters, setChapters] = useState([]);
    const [selectedChapters, setSelectedChapters] = useState([]);

    // Generation state
    const [generatedQuestions, setGeneratedQuestions] = useState([]);
    const [generationErrors, setGenerationErrors] = useState([]);

    // Import state
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [creatingCategory, setCreatingCategory] = useState(false);

    // Last analysis history (array of last 3)
    const [analysisHistory, setAnalysisHistory] = useState([]);

    // Auto-loop state
    const [autoLoopMode, setAutoLoopMode] = useState(false);
    const [autoLoopConfig, setAutoLoopConfig] = useState({
        totalPages: 50,
        batchSize: 5
    });
    const [autoLoopStatus, setAutoLoopStatus] = useState(null); // null | 'running' | 'done' | 'error'
    const [autoLoopResults, setAutoLoopResults] = useState(null);

    // QGen toggle — send to review instead of main bank
    const [useQgen, setUseQgen] = useState(false);

    // Clinical cases generation mode
    const [generationMode, setGenerationMode] = useState('regular'); // 'regular' | 'clinical'
    const [clinicalSettings, setClinicalSettings] = useState({
        difficulty: 'medium',
        focus_tag: 'diagnosis',
        multi_step: false
    });

    // Load analysis history from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('pdfGeneratorHistory');
        if (saved) {
            try {
                setAnalysisHistory(JSON.parse(saved));
            } catch (e) {
                console.error('Failed to parse analysis history', e);
            }
        }
    }, []);

    // Save analysis to localStorage (keeps last 3)
    const saveAnalysisHistory = (filename, startPage, pageCount, chaptersCount, questionsImported = null) => {
        const analysisInfo = {
            filename,
            startPage,
            endPage: startPage + (pageCount || 0),
            pageCount,
            chaptersCount,
            questionsImported,
            date: new Date().toISOString()
        };
        const newHistory = [analysisInfo, ...analysisHistory].slice(0, 3);
        localStorage.setItem('pdfGeneratorHistory', JSON.stringify(newHistory));
        setAnalysisHistory(newHistory);
    };

    // Admin check
    if (user?.role !== 'admin') {
        return (
            <Box sx={{ p: 3 }}>
                <Alert color="danger">Access denied. Admin only.</Alert>
            </Box>
        );
    }

    // Handlers
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
            setError('');
        } else {
            setError('Please select a valid PDF file');
        }
    };

    const handleUpload = async () => {
        if (!pdfFile) return;

        setLoading(true);
        setError('');

        try {
            const response = await APIUploadPDF(token, pdfFile);
            if (response.success) {
                setPdfInfo(response);
                setStep(2);

                // Auto-fill startPage based on last analysis of same file
                const lastAnalysis = analysisHistory[0];
                if (lastAnalysis && lastAnalysis.filename === pdfFile.name) {
                    const suggestedStart = lastAnalysis.endPage + 1;
                    if (suggestedStart < response.total_pages) {
                        setStartPage(suggestedStart);
                    }
                }

                // Load categories for later
                const catResponse = await APIGetContentTree(token);
                if (catResponse.categories) {
                    setCategories(flattenCategories(catResponse.categories));
                }
            } else {
                setError(response.error);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Upload failed');
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async () => {
        setLoading(true);
        setError('');

        // Determine actual page count (custom or preset)
        const actualPageCount = pageCount === 'custom'
            ? (parseInt(customPageCount, 10) || null)
            : pageCount;

        try {
            const response = await APIAnalyzePDFPages(token, pdfInfo.pdf_path, startPage, actualPageCount);
            if (response.success) {
                setChapters(response.chapters);
                setSelectedChapters(response.chapters.map((_, i) => i));
                // Save analysis history
                saveAnalysisHistory(pdfInfo.filename, startPage, actualPageCount, response.chapters.length);
                setStep(3);
            } else {
                setError(response.error);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Analysis failed');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        setLoading(true);
        setError('');
        setGenerationErrors([]);

        try {
            let response;

            if (generationMode === 'clinical') {
                // Generate clinical cases
                response = await APIGenerateClinicalCases(token, {
                    chapters: selectedChapters.map(i => chapters[i]),
                    difficulty: clinicalSettings.difficulty,
                    focus_tag: clinicalSettings.focus_tag,
                    multi_step: clinicalSettings.multi_step
                });
            } else {
                // Generate regular questions
                response = await APIGenerateQuestions(token, chapters, selectedChapters);
            }

            if (response.success) {
                setGeneratedQuestions(response.questions || response.generated_cases || []);
                if (response.errors) {
                    setGenerationErrors(response.errors);
                }
                setStep(4);
            } else {
                setError(response.error);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Generation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        setLoading(true);
        setError('');

        try {
            let response;

            if (generationMode === 'clinical') {
                // Import clinical cases
                response = await APIImportClinicalCases(token, generatedQuestions);
            } else if (useQgen) {
                // Import to QGen for review
                const batchTitle = pdfInfo?.filename
                    ? `PDF: ${pdfInfo.filename} (стр. ${startPage + 1}-${startPage + (pageCount || 0)})`
                    : 'PDF Generation';
                response = await APIImportToQgen(token, generatedQuestions, batchTitle);
            } else {
                // Import regular questions directly to main bank
                const categoryIds = selectedCategory ? [selectedCategory] : [];
                response = await APIImportGeneratedQuestions(token, generatedQuestions, categoryIds);
            }

            if (response.success) {
                setImportResult({ ...response, wasQgen: useQgen });
            } else {
                setError(response.error);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Import failed');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        try {
            await APICleanupGenerator(token, pdfInfo?.pdf_path);
        } catch (err) {
            console.error('Cleanup error:', err);
        }

        setStep(1);
        setPdfFile(null);
        setPdfInfo(null);
        setChapters([]);
        setSelectedChapters([]);
        setGeneratedQuestions([]);
        setGenerationErrors([]);
        setImportResult(null);
        setError('');

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;

        setCreatingCategory(true);
        try {
            const response = await APICreateCategory(token, {
                name: newCategoryName.trim(),
                description: '',
                parent: null
            });
            if (response.success !== false && response.id) {
                // Reload categories
                const catResponse = await APIGetContentTree(token);
                if (catResponse.categories) {
                    setCategories(flattenCategories(catResponse.categories));
                }
                // Select the new category
                setSelectedCategory(response.id);
                setNewCategoryName('');
            } else {
                setError(response.error || 'Failed to create category');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create category');
        } finally {
            setCreatingCategory(false);
        }
    };

    const handleAutoLoop = async () => {
        setAutoLoopStatus('running');
        setAutoLoopResults(null);
        setError('');

        try {
            const results = await APIGenerateLoop(
                token,
                pdfInfo.pdf_path,
                startPage,
                autoLoopConfig.totalPages,
                autoLoopConfig.batchSize
            );

            setAutoLoopResults(results);
            setAutoLoopStatus('done');

            // Update analysis history
            saveAnalysisHistory(
                pdfInfo.filename,
                startPage,
                autoLoopConfig.totalPages,
                results.batches_processed,
                results.total_questions_imported
            );

        } catch (err) {
            console.error('Auto-loop error:', err);
            setAutoLoopStatus('error');
            setAutoLoopResults({ error: err.response?.data?.error || err.message });
            setError(err.response?.data?.error || 'Auto-loop failed');
        }
    };

    const flattenCategories = (cats, prefix = '') => {
        let result = [];
        cats.forEach(cat => {
            result.push({ id: cat.id, name: prefix + cat.name });
            if (cat.children?.length > 0) {
                result = [...result, ...flattenCategories(cat.children, prefix + cat.name + ' → ')];
            }
        });
        return result;
    };

    const toggleChapter = (index) => {
        setSelectedChapters(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography level="h2">PDF Question Generator</Typography>
                    {step > 1 && (
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<Trash size={18} />}
                            onClick={handleReset}
                        >
                            Start Over
                        </Button>
                    )}
                </Box>

                {/* Progress indicator */}
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    {['Upload PDF', 'Analyze', 'Generate', 'Import'].map((label, i) => (
                        <Chip
                            key={label}
                            variant={step > i + 1 ? 'solid' : step === i + 1 ? 'soft' : 'outlined'}
                            color={step > i + 1 ? 'success' : step === i + 1 ? 'primary' : 'neutral'}
                        >
                            {i + 1}. {label}
                        </Chip>
                    ))}
                </Stack>

                {error && <Alert color="danger">{error}</Alert>}
                {loading && <LinearProgress />}

                {/* Step 1: Upload */}
                {step === 1 && (
                    <Sheet variant="outlined" sx={{ p: 4, borderRadius: 'lg' }}>
                        <Stack spacing={3} alignItems="center">
                            {/* Analysis History (last 3) */}
                            {analysisHistory.length > 0 && (
                                <Card variant="soft" sx={{ width: '100%', p: 2 }}>
                                    <Typography level="title-sm" sx={{ mb: 1.5 }}>📋 История генераций:</Typography>
                                    <Stack spacing={1.5}>
                                        {analysisHistory.map((analysis, index) => (
                                            <Sheet
                                                key={index}
                                                variant={index === 0 ? "outlined" : "plain"}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 'sm',
                                                    bgcolor: index === 0 ? 'background.surface' : 'transparent',
                                                    opacity: index === 0 ? 1 : 0.7
                                                }}
                                            >
                                                <Stack spacing={0.5}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography level="body-sm" fontWeight={600}>
                                                            {analysis.filename}
                                                        </Typography>
                                                        <Typography level="body-xs" color="neutral">
                                                            {new Date(analysis.date).toLocaleString('ru-RU')}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={2}>
                                                        <Typography level="body-xs">
                                                            Стр. {analysis.startPage + 1}—{analysis.endPage + 1}
                                                            {analysis.pageCount && ` (${analysis.pageCount})`}
                                                        </Typography>
                                                        <Typography level="body-xs">
                                                            Глав: {analysis.chaptersCount}
                                                        </Typography>
                                                        {analysis.questionsImported && (
                                                            <Typography level="body-xs" color="success">
                                                                ✓ {analysis.questionsImported} вопросов
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                    {index === 0 && (
                                                        <Typography level="body-sm" sx={{ mt: 0.5, color: '#DA7756' }}>
                                                            💡 Продолжить с страницы <strong>{analysis.endPage + 2}</strong>
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Sheet>
                                        ))}
                                    </Stack>
                                </Card>
                            )}

                            <UploadSimple size={60} />
                            <Typography level="h4">Upload Medical Textbook PDF</Typography>
                            <Typography level="body-md" color="neutral">
                                Text-based PDF (not scanned). Chapters detected by page headers.
                            </Typography>

                            <input
                                type="file"
                                accept=".pdf"
                                onChange={handleFileSelect}
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                            />

                            <Button
                                size="lg"
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Select PDF File
                            </Button>

                            {pdfFile && (
                                <Alert color="success" sx={{ width: '100%' }}>
                                    Selected: <strong>{pdfFile.name}</strong> ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                                </Alert>
                            )}

                            <Button
                                size="lg"
                                disabled={!pdfFile || loading}
                                loading={loading}
                                onClick={handleUpload}
                                startDecorator={<UploadSimple size={18} />}
                            >
                                Upload & Continue
                            </Button>
                        </Stack>
                    </Sheet>
                )}

                {/* Step 2: Analyze */}
                {step === 2 && pdfInfo && (
                    <Sheet variant="outlined" sx={{ p: 4, borderRadius: 'lg' }}>
                        <Stack spacing={3}>
                            <Alert color="success">
                                <strong>{pdfInfo.filename}</strong> uploaded successfully. Total pages: {pdfInfo.total_pages}
                            </Alert>

                            <Typography level="h4">Select Page Range to Analyze</Typography>

                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6}>
                                    <FormControl>
                                        <FormLabel>Start Page</FormLabel>
                                        <Select
                                            value={startPage}
                                            onChange={(e, val) => setStartPage(val)}
                                        >
                                            {Array.from({ length: pdfInfo.total_pages }, (_, i) => (
                                                <Option key={i} value={i}>Page {i + 1}</Option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                    <FormControl>
                                        <FormLabel>Number of Pages</FormLabel>
                                        <Stack direction="row" spacing={1}>
                                            <Select
                                                value={pageCount}
                                                onChange={(e, val) => setPageCount(val)}
                                                sx={{ flex: 1 }}
                                            >
                                                {PAGE_COUNT_OPTIONS.map(opt => (
                                                    <Option key={opt.label} value={opt.value}>{opt.label}</Option>
                                                ))}
                                            </Select>
                                            {pageCount === 'custom' && (
                                                <Input
                                                    type="number"
                                                    placeholder="Enter number"
                                                    value={customPageCount}
                                                    onChange={(e) => setCustomPageCount(e.target.value)}
                                                    sx={{ width: 120 }}
                                                    slotProps={{ input: { min: 1, max: pdfInfo?.total_pages } }}
                                                />
                                            )}
                                        </Stack>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Divider>Режим генерации</Divider>

                            <Stack direction="row" spacing={2}>
                                <Button
                                    size="lg"
                                    variant={!autoLoopMode ? 'solid' : 'outlined'}
                                    color={!autoLoopMode ? 'primary' : 'neutral'}
                                    onClick={() => setAutoLoopMode(false)}
                                    sx={{ flex: 1 }}
                                >
                                    Ручной режим
                                </Button>
                                <Button
                                    size="lg"
                                    variant={autoLoopMode ? 'solid' : 'outlined'}
                                    color={autoLoopMode ? 'primary' : 'neutral'}
                                    onClick={() => setAutoLoopMode(true)}
                                    startDecorator={<ArrowsClockwise size={18} />}
                                    sx={{ flex: 1 }}
                                >
                                    Auto-Loop
                                </Button>
                            </Stack>

                            {!autoLoopMode ? (
                                <Button
                                    size="lg"
                                    disabled={loading}
                                    loading={loading}
                                    onClick={handleAnalyze}
                                    startDecorator={<ChartLine size={18} />}
                                >
                                    Analyze Pages
                                </Button>
                            ) : (
                                <Card variant="soft" sx={{ p: 2 }}>
                                    <Stack spacing={2}>
                                        <Alert color="primary" size="sm">
                                            🔄 Auto-Loop автоматически обработает страницы батчами:
                                            анализ → генерация → импорт → повтор
                                        </Alert>

                                        <Grid container spacing={2}>
                                            <Grid xs={12} sm={6}>
                                                <FormControl>
                                                    <FormLabel>Количество страниц</FormLabel>
                                                    <Input
                                                        type="number"
                                                        value={autoLoopConfig.totalPages}
                                                        onChange={(e) => setAutoLoopConfig(prev => ({
                                                            ...prev,
                                                            totalPages: parseInt(e.target.value) || 50
                                                        }))}
                                                        slotProps={{ input: { min: 1, max: pdfInfo.total_pages - startPage } }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid xs={12} sm={6}>
                                                <FormControl>
                                                    <FormLabel>Страниц в батче</FormLabel>
                                                    <Input
                                                        type="number"
                                                        value={autoLoopConfig.batchSize}
                                                        onChange={(e) => setAutoLoopConfig(prev => ({
                                                            ...prev,
                                                            batchSize: parseInt(e.target.value) || 5
                                                        }))}
                                                        slotProps={{ input: { min: 1, max: 20 } }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>

                                        <Typography level="body-sm" color="neutral">
                                            Будет обработано: страницы {startPage + 1} — {Math.min(startPage + autoLoopConfig.totalPages, pdfInfo.total_pages)}
                                            ({Math.ceil(autoLoopConfig.totalPages / autoLoopConfig.batchSize)} батчей)
                                        </Typography>

                                        <Stack direction="row" spacing={2}>
                                            <Button
                                                size="lg"
                                                disabled={autoLoopStatus === 'running'}
                                                loading={autoLoopStatus === 'running'}
                                                onClick={handleAutoLoop}
                                                startDecorator={<ArrowsClockwise size={18} />}
                                                color="success"
                                                sx={{ flex: 1 }}
                                            >
                                                {autoLoopStatus === 'running' ? 'Обработка...' : 'Запустить Auto-Loop'}
                                            </Button>
                                            {autoLoopStatus === 'running' && (
                                                <Button
                                                    size="lg"
                                                    variant="outlined"
                                                    color="danger"
                                                    onClick={() => {
                                                        setAutoLoopStatus('cancelled');
                                                        setError('Генерация отменена пользователем');
                                                    }}
                                                >
                                                    Отменить
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Card>
                            )}

                            {/* Auto-Loop Results */}
                            {autoLoopStatus === 'done' && autoLoopResults && (
                                <Card variant="outlined" sx={{ mt: 2 }}>
                                    <Stack spacing={2} sx={{ p: 2 }}>
                                        <Alert color="success">
                                            <Typography level="title-md">✅ Auto-Loop завершен!</Typography>
                                        </Alert>

                                        <Grid container spacing={2}>
                                            <Grid xs={6} sm={3}>
                                                <Typography level="body-xs" color="neutral">Батчей</Typography>
                                                <Typography level="title-lg">{autoLoopResults.batches_processed}</Typography>
                                            </Grid>
                                            <Grid xs={6} sm={3}>
                                                <Typography level="body-xs" color="neutral">Вопросов</Typography>
                                                <Typography level="title-lg">{autoLoopResults.total_questions_imported}</Typography>
                                            </Grid>
                                            <Grid xs={6} sm={3}>
                                                <Typography level="body-xs" color="neutral">Время</Typography>
                                                <Typography level="title-lg">{autoLoopResults.total_duration_sec}с</Typography>
                                            </Grid>
                                            <Grid xs={6} sm={3}>
                                                <Typography level="body-xs" color="neutral">Период</Typography>
                                                <Typography level="body-sm">{autoLoopResults.started_at} — {autoLoopResults.finished_at}</Typography>
                                            </Grid>
                                        </Grid>

                                        {autoLoopResults.progress?.length > 0 && (
                                            <Box>
                                                <Typography level="title-sm" sx={{ mb: 1 }}>Детали по батчам:</Typography>
                                                <Sheet variant="soft" sx={{ p: 1, borderRadius: 'sm', maxHeight: 200, overflow: 'auto' }}>
                                                    {autoLoopResults.progress.map((p, i) => (
                                                        <Typography key={i} level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                            #{p.batch} [{p.started_at}→{p.finished_at}] {p.duration_sec}с | стр.{p.pages} | {p.status === 'success' ? `✓ ${p.questions_generated} вопр.` : `⊘ ${p.reason}`}
                                                        </Typography>
                                                    ))}
                                                </Sheet>
                                            </Box>
                                        )}

                                        {autoLoopResults.errors?.length > 0 && (
                                            <Alert color="warning" size="sm">
                                                Ошибок: {autoLoopResults.errors.length}
                                            </Alert>
                                        )}

                                        {/* Кнопка продолжить */}
                                        <Divider />
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Button
                                                size="lg"
                                                color="primary"
                                                startDecorator={<ArrowsClockwise size={18} />}
                                                onClick={() => {
                                                    const nextStart = startPage + autoLoopConfig.totalPages;
                                                    if (nextStart < pdfInfo.total_pages) {
                                                        setStartPage(nextStart);
                                                        setAutoLoopStatus(null);
                                                        setAutoLoopResults(null);
                                                    }
                                                }}
                                                disabled={startPage + autoLoopConfig.totalPages >= pdfInfo.total_pages}
                                            >
                                                Продолжить: стр. {startPage + autoLoopConfig.totalPages + 1} → {Math.min(startPage + autoLoopConfig.totalPages * 2, pdfInfo.total_pages)}
                                            </Button>
                                            <Typography level="body-sm" color="neutral">
                                                Осталось: {Math.max(0, pdfInfo.total_pages - startPage - autoLoopConfig.totalPages)} стр.
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Card>
                            )}

                            {autoLoopStatus === 'error' && autoLoopResults && (
                                <Alert color="danger" sx={{ mt: 2 }}>
                                    <Typography level="body-sm">
                                        Ошибка: {autoLoopResults.error || 'Unknown error'}
                                    </Typography>
                                </Alert>
                            )}
                        </Stack>
                    </Sheet>
                )}

                {/* Step 3: Generate */}
                {step === 3 && chapters.length > 0 && (
                    <Sheet variant="outlined" sx={{ p: 4, borderRadius: 'lg' }}>
                        <Stack spacing={3}>
                            <Typography level="h4">
                                Detected {chapters.length} Chapters/Sections
                            </Typography>

                            {/* Generation Mode Toggle */}
                            <FormControl>
                                <FormLabel>Режим генерации</FormLabel>
                                <Select
                                    value={generationMode}
                                    onChange={(e, value) => setGenerationMode(value)}
                                >
                                    <Option value="regular">Обычные вопросы</Option>
                                    <Option value="clinical">Клинические задачи</Option>
                                </Select>
                            </FormControl>

                            {/* Clinical Cases Settings */}
                            {generationMode === 'clinical' && (
                                <Card variant="outlined" sx={{ p: 2 }}>
                                    <Stack spacing={2}>
                                        <Typography level="title-sm">
                                            Настройки клинических задач
                                        </Typography>

                                        <FormControl size="sm">
                                            <FormLabel>Сложность</FormLabel>
                                            <Select
                                                value={clinicalSettings.difficulty}
                                                onChange={(e, value) => setClinicalSettings(prev => ({ ...prev, difficulty: value }))}
                                            >
                                                <Option value="easy">Легкая</Option>
                                                <Option value="medium">Средняя</Option>
                                                <Option value="hard">Сложная</Option>
                                            </Select>
                                        </FormControl>

                                        <FormControl size="sm">
                                            <FormLabel>Фокус задачи</FormLabel>
                                            <Select
                                                value={clinicalSettings.focus_tag}
                                                onChange={(e, value) => setClinicalSettings(prev => ({ ...prev, focus_tag: value }))}
                                            >
                                                <Option value="diagnosis">Диагноз</Option>
                                                <Option value="pathogenesis">Этиология/Патогенез</Option>
                                                <Option value="treatment">Лечение</Option>
                                            </Select>
                                        </FormControl>

                                        <FormControl size="sm">
                                            <Checkbox
                                                label="Многошаговый случай (3-5 вопросов на один кейс)"
                                                checked={clinicalSettings.multi_step}
                                                onChange={(e) => setClinicalSettings(prev => ({ ...prev, multi_step: e.target.checked }))}
                                            />
                                        </FormControl>
                                    </Stack>
                                </Card>
                            )}

                            <Typography level="body-sm" color="neutral">
                                Select chapters to generate {generationMode === 'clinical' ? 'clinical cases' : 'questions'} from:
                            </Typography>

                            <List>
                                {chapters.map((ch, i) => (
                                    <ListItem key={i}>
                                        <Checkbox
                                            checked={selectedChapters.includes(i)}
                                            onChange={() => toggleChapter(i)}
                                        />
                                        <ListItemContent>
                                            <Typography level="title-sm">
                                                {ch.section} {ch.chapter && `- ${ch.chapter}`}
                                            </Typography>
                                            <Typography level="body-xs" color="neutral">
                                                Pages: {ch.pages.join(', ')} ({ch.page_count} pages)
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                ))}
                            </List>

                            <Button
                                size="lg"
                                disabled={selectedChapters.length === 0 || loading}
                                loading={loading}
                                onClick={handleGenerate}
                                startDecorator={<Sparkle size={18} />}
                            >
                                {generationMode === 'clinical'
                                    ? `Generate Clinical Cases (${selectedChapters.length} chapters)`
                                    : `Generate Questions (${selectedChapters.length} chapters)`}
                            </Button>
                        </Stack>
                    </Sheet>
                )}

                {/* Step 4: Import */}
                {step === 4 && (
                    <Sheet variant="outlined" sx={{ p: 4, borderRadius: 'lg' }}>
                        <Stack spacing={3}>
                            {!importResult ? (
                                <>
                                    <Alert color="success">
                                        Generated <strong>{generatedQuestions.length}</strong> questions!
                                    </Alert>

                                    {generationErrors.length > 0 && (
                                        <Alert color="warning">
                                            {generationErrors.length} errors during generation
                                        </Alert>
                                    )}

                                    <Typography level="h4">Preview Questions</Typography>

                                    <AccordionGroup sx={{ maxHeight: 400, overflow: 'auto' }}>
                                        {generatedQuestions.slice(0, 10).map((q, i) => (
                                            <Accordion key={i}>
                                                <AccordionSummary>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                                        {q.category && (
                                                            <Chip size="sm" variant="soft" color="primary" sx={{ flexShrink: 0 }}>
                                                                {q.category}
                                                            </Chip>
                                                        )}
                                                        <Typography level="body-sm" noWrap sx={{ flex: 1 }}>
                                                            {i + 1}. {q.question}
                                                        </Typography>
                                                    </Stack>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Stack spacing={1}>
                                                        {q.category && (
                                                            <Typography level="body-xs" color="primary">
                                                                📁 Category: {q.category}
                                                            </Typography>
                                                        )}
                                                        <Typography level="body-xs">A: {q.answer_a}</Typography>
                                                        <Typography level="body-xs">B: {q.answer_b}</Typography>
                                                        <Typography level="body-xs">C: {q.answer_c}</Typography>
                                                        <Typography level="body-xs">D: {q.answer_d}</Typography>
                                                        <Typography level="body-xs">E: {q.answer_e}</Typography>
                                                        <Chip size="sm" color="success">Correct: {q.correct}</Chip>
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </AccordionGroup>

                                    {generatedQuestions.length > 10 && (
                                        <Typography level="body-sm" color="neutral">
                                            ... and {generatedQuestions.length - 10} more questions
                                        </Typography>
                                    )}

                                    <Divider />

                                    {/* Category Selection Section */}
                                    <Card variant="soft" sx={{ p: 2 }}>
                                        <Stack spacing={2}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Folder size={18} />
                                                <Typography level="title-md">Категория для импорта</Typography>
                                            </Stack>

                                            <Alert color="primary" size="sm" variant="soft">
                                                💡 AI автоматически определяет категорию для каждого вопроса.
                                                Если категория не существует — она будет создана автоматически.
                                                Выбор ниже используется как fallback.
                                            </Alert>

                                            <FormControl>
                                                <FormLabel>Существующая категория</FormLabel>
                                                <Select
                                                    placeholder="Выберите категорию..."
                                                    value={selectedCategory}
                                                    onChange={(e, val) => setSelectedCategory(val)}
                                                >
                                                    <Option value={null}>— Без категории —</Option>
                                                    {categories.map(cat => (
                                                        <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <Divider>или</Divider>

                                            <FormControl>
                                                <FormLabel>Создать новую категорию</FormLabel>
                                                <Stack direction="row" spacing={1}>
                                                    <Input
                                                        placeholder="Название новой категории..."
                                                        value={newCategoryName}
                                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <Button
                                                        variant="soft"
                                                        color="primary"
                                                        disabled={!newCategoryName.trim() || creatingCategory}
                                                        loading={creatingCategory}
                                                        onClick={handleCreateCategory}
                                                        startDecorator={<Plus size={18} />}
                                                    >
                                                        Создать
                                                    </Button>
                                                </Stack>
                                            </FormControl>

                                            {selectedCategory && (
                                                <Alert color="primary" size="sm">
                                                    Вопросы будут импортированы в: <strong>{categories.find(c => c.id === selectedCategory)?.name}</strong>
                                                </Alert>
                                            )}
                                        </Stack>
                                    </Card>

                                    {generationMode !== 'clinical' && (
                                        <Card variant="soft" color={useQgen ? 'warning' : 'neutral'} sx={{ p: 2 }}>
                                            <Checkbox
                                                checked={useQgen}
                                                onChange={(e) => setUseQgen(e.target.checked)}
                                                label="QGen — отправить на ревью (Задачи → Сгенерированные)"
                                                size="sm"
                                                sx={{ fontWeight: 600 }}
                                            />
                                            <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                                                {useQgen
                                                    ? 'Вопросы попадут в раздел Задачи → Сгенерированные для проверки перед импортом в основной банк'
                                                    : 'Вопросы будут импортированы напрямую в основной банк вопросов'}
                                            </Typography>
                                        </Card>
                                    )}

                                    <Button
                                        size="lg"
                                        disabled={loading}
                                        loading={loading}
                                        onClick={handleImport}
                                        color={useQgen ? 'warning' : 'primary'}
                                        startDecorator={<FloppyDisk size={18} />}
                                    >
                                        {useQgen
                                            ? `Отправить на ревью (${generatedQuestions.length} вопросов)`
                                            : `Импортировать ${generatedQuestions.length} вопросов`}
                                    </Button>
                                </>
                            ) : (
                                <Stack spacing={2} alignItems="center">
                                    <CheckCircle size={80} weight="fill" />
                                    <Typography level="h3" color="success">
                                        {importResult.wasQgen ? 'Отправлено на ревью!' : 'Импорт завершён!'}
                                    </Typography>
                                    <Typography level="body-lg">
                                        {importResult.wasQgen
                                            ? `${importResult.imported_count} вопросов отправлено в Задачи → Сгенерированные`
                                            : `${importResult.imported_count} вопросов добавлено в APEX QBANK`}
                                    </Typography>

                                    {/* Debug: show raw importResult */}
                                    <Alert color="neutral" sx={{ width: '100%' }}>
                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(importResult, null, 2)}
                                        </Typography>
                                    </Alert>

                                    {/* Categories where questions were imported */}
                                    {importResult.categories_used && importResult.categories_used.length > 0 && (
                                        <Alert color="success" sx={{ width: '100%' }}>
                                            <Stack spacing={1}>
                                                <Typography level="title-sm">📁 Вопросы импортированы в категории:</Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    {importResult.categories_used.map((cat, i) => (
                                                        <Chip key={i} size="sm" variant="soft" color="success">{cat}</Chip>
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        </Alert>
                                    )}

                                    {/* Uncategorized count warning */}
                                    {importResult.uncategorized_count > 0 && (
                                        <Alert color="warning" sx={{ width: '100%' }}>
                                            <Typography level="body-sm">
                                                ⚠️ {importResult.uncategorized_count} вопросов не удалось категоризировать (добавлены в "Uncategorized")
                                            </Typography>
                                        </Alert>
                                    )}

                                    <Button onClick={handleReset}>
                                        Генерировать ещё
                                    </Button>
                                </Stack>
                            )}
                        </Stack>
                    </Sheet>
                )}
            </Stack>
        </Box>
    );
}
